import React, { Suspense } from "react";
import { NavLink, Outlet } from "react-router-dom";
import Icon from "./Icon";
import { PageSpinner } from "./primitives";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { useUnread } from "../context/UnreadContext";
import { useUnreadChats } from "../context/UnreadChatsContext";
import { APP_STORE_URL, UNIVERSITY } from "../lib/constants";
import useRouteFade from "../lib/useRouteFade";

// Sidebar ids are `side.*`; the tab bar reuses the mobile tab ids (`tab.<RouteName>`, Clubs = Home)
// so both can be in the DOM at once without clashing.
const NAV = [
  ["/clubs", "grid", "Clubs", "side.clubs"],
  ["/events", "cal", "Events", "side.events"],
  ["/chats", "chat", "Chats", "side.chats"],
  ["/partners", "tag", "Partners", "side.partners"],
  ["/profile", "user", "Profile", "side.profile"],
];
// Mobile tab bar order from the mockup: Clubs is the raised center button.
const TABS = [
  ["/partners", "tag", "Partners", "tab.Partners"],
  ["/events", "cal", "Events", "tab.Events"],
  ["/clubs", "grid", "Clubs", "tab.Home"],
  ["/chats", "chat", "Chats", "tab.Chats"],
  ["/profile", "user", "Profile", "tab.Profile"],
];

const badgeText = (n) => (n > 99 ? "99+" : String(n));
// `badge` is a count, or UNKNOWN while the unread listener is failing (stale is not "0").
const UNKNOWN = "unknown";

function SideLink({ to, icon, label, badge, testId }) {
  const unknown = badge === UNKNOWN;
  return (
    <NavLink to={to} data-testid={testId} className={({ isActive }) => `nav${isActive ? " on" : ""}`}>
      <Icon name={icon} />
      {label}
      {unknown && <span className="count" title="Unread count unavailable" aria-label="unread count unavailable">?</span>}
      {!unknown && badge > 0 && <span className="count">{badgeText(badge)}</span>}
    </NavLink>
  );
}

/**
 * Authenticated layout: sidebar (>= 820px) / bottom tab bar (< 820px) around `<main class="main">`,
 * which renders the matched child route via <Outlet/>.
 */
export default function Shell() {
  const { isSuperAdmin, signOut } = useAuth();
  const { leadsAnyClub } = useData();
  const fadeRef = useRouteFade();
  const unread = useUnread();
  const chatsCtx = useUnreadChats();
  // The Chats badge also counts DM requests and incoming connections: unknown while any of the
  // listeners feeding it is down.
  const chatsBadge = unread.unreadError || chatsCtx?.badgeError ? UNKNOWN : unread.chats;
  // Pending incoming connection requests: read from the C1 listener, never a second subscription.
  const friendsBadge = chatsCtx?.connectionsError ? UNKNOWN : chatsCtx?.incomingConnections ?? 0;

  return (
    <div className="app">
      <aside className="side" aria-label="Main navigation">
        <div className="brand">
          <div className="brand-mark">V</div>
          <div><b>VibeLink</b><small>{UNIVERSITY}</small></div>
        </div>
        {NAV.map(([to, icon, label, testId]) => (
          <SideLink key={to} to={to} icon={icon} label={label} testId={testId} badge={to === "/chats" ? chatsBadge : 0} />
        ))}
        <div className="nav-sec">You</div>
        <SideLink to="/notifications" icon="bell" label="Notifications" testId="side.notifications" badge={unread.unreadError ? UNKNOWN : unread.notifications} />
        <SideLink to="/friends" icon="users" label="Friends" testId="side.friends" badge={friendsBadge} />
        <SideLink to="/settings" icon="gear" label="Settings" testId="side.settings" />
        {(leadsAnyClub || isSuperAdmin) && <div className="nav-sec">Leadership</div>}
        {leadsAnyClub && <SideLink to="/manage" icon="shield" label="Manage Club" testId="side.manageClub" />}
        {isSuperAdmin && <SideLink to="/super" icon="star" label="Super Admin" testId="side.superAdmin" />}
        <div className="side-foot">
          <a className="nav" style={{ fontSize: 13 }} href={APP_STORE_URL} data-testid="side.getApp" target="_blank" rel="noopener noreferrer">
            <Icon name="phone" />Get the iOS app
          </a>
          <button type="button" className="nav" style={{ fontSize: 13 }} data-testid="side.signOut" onClick={() => signOut()}>
            <Icon name="logout" />Sign out
          </button>
        </div>
      </aside>
      {/* Lazy section pages suspend here, so the sidebar / tab bar stay put while a chunk loads. */}
      <main className="main" id="main" ref={fadeRef}><Suspense fallback={<PageSpinner />}><Outlet /></Suspense></main>
      <nav className="tabbar" aria-label="Tabs">
        {TABS.map(([to, icon, label, testId]) => {
          const badge = to === "/chats" ? chatsBadge : 0;
          const unknown = badge === UNKNOWN;
          return (
            <NavLink
              key={to}
              to={to}
              data-testid={testId}
              className={({ isActive }) => (isActive ? "on" : "")}
              aria-label={unknown ? `${label}, unread count unavailable` : badge > 0 ? `${label}, ${badge} unread` : undefined}
            >
              {to === "/clubs" ? <span className="mid"><Icon name={icon} size={24} /></span> : <Icon name={icon} size={22} />}
              {label}
              {unknown && <span className="tbadge" aria-hidden="true">?</span>}
              {!unknown && badge > 0 && <span className="tbadge" aria-hidden="true">{badgeText(badge)}</span>}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
